import { reactive, computed } from 'vue'
import UserConfigService from '@/core/services/UserConfigService'
import ApiService from '@/core/services/ApiService'
import { endpoints } from '@/core/services/Endpoints'
import fileManagerPicker from '@/components/filemanager/fileManagerPicker'

const RADIO_API = 'https://all.api.radio-browser.info/json'
const NAMESPACE = 'musicPanel'
const MAX_RECENT = 10
const MAX_FAVORITES = 50
const DEFAULT_PAGE_LIMIT = 30

const GENRE_TAGS = {
    pop: 'pop',
    rock: 'rock',
    turkish: 'turkish',
    jazz: 'jazz',
    classical: 'classical',
    folk: 'folk',
    arabesk: 'arabesk',
    news: 'news',
}

const state = reactive({
    playing: false,
    currentStation: null,
    currentTrack: null,
    volume: 0.7,
    muted: false,
    dockAnimation: true,
    duration: 0,
    currentTime: 0,
    buffering: false,
    error: null,

    stations: [],
    favorites: [],
    recentStations: [],
    radioCategory: 'favorites',
    radioGenre: null,
    radioLoading: false,
    radioSearchQuery: '',

    playlists: [],
    libraryTracks: [],
    activePlaylistId: null,
    libraryPage: 1,
    libraryHasMore: false,
    libraryLoading: false,
    _pageLimit: DEFAULT_PAGE_LIMIT,
    librarySearchQuery: '',
    playlistsLoading: false,

    activeTab: 'radio',
    favoriteTracks: [],
    shuffle: false,
    repeat: 'off',

    selectMode: false,
    selectedTrackIds: [],

    _initialized: false,
    _libraryFetched: false,
})

let _audio = null
let _saveTimeout = null
let _trackLoadId = 0

function _getAudio() {
    if (!_audio) {
        _audio = new Audio()
        _audio.volume = state.muted ? 0 : state.volume

        _audio.addEventListener('play', () => {
            state.playing = true
            state.error = null
        })
        _audio.addEventListener('pause', () => {
            state.playing = false
        })
        _audio.addEventListener('waiting', () => {
            state.buffering = true
        })
        _audio.addEventListener('canplay', () => {
            state.buffering = false
        })
        _audio.addEventListener('playing', () => {
            state.buffering = false
        })
        _audio.addEventListener('error', () => {
            state.playing = false
            state.buffering = false
            state.error = 'streamError'
        })
        _audio.addEventListener('ended', () => {
            if (state.currentTrack) {
                if (state.repeat === 'one') {
                    seek(0)
                    _audio.play().catch(() => { /* empty */ })
                } else {
                    nextTrack()
                }
            } else {
                state.playing = false
            }
        })
        _audio.addEventListener('timeupdate', () => {
            if (_audio) {
                state.currentTime = _audio.currentTime || 0
            }
        })
        _audio.addEventListener('loadedmetadata', () => {
            if (_audio && isFinite(_audio.duration)) {
                state.duration = _audio.duration
            }
        })
    }
    return _audio
}

function _loadSaved() {
    const saved = UserConfigService.get(NAMESPACE)
    state.favorites = saved.favorites || []
    state.recentStations = saved.recentStations || []
    state.volume = saved.volume ?? 0.7
    state.muted = saved.muted ?? false
    state.activeTab = saved.activeTab || 'radio'
    state.radioCategory = saved.radioCategory || 'favorites'
    state.activePlaylistId = saved.activePlaylistId || null
    state.favoriteTracks = saved.favoriteTracks || []
    state.shuffle = saved.shuffle ?? false
    state.repeat = saved.repeat || 'off'
    state.dockAnimation = saved.dockAnimation ?? true

    state._lastStation = saved.lastStation || null
}

function _save() {
    clearTimeout(_saveTimeout)
    _saveTimeout = setTimeout(() => {
        const data = {
            favorites: state.favorites,
            recentStations: state.recentStations,
            volume: state.volume,
            muted: state.muted,
            activeTab: state.activeTab,
            radioCategory: state.radioCategory,
            activePlaylistId: state.activePlaylistId,
            favoriteTracks: state.favoriteTracks,
            shuffle: state.shuffle,
            repeat: state.repeat,
            dockAnimation: state.dockAnimation,
        }
        if (state.currentStation) {
            data.lastStation = {
                stationuuid: state.currentStation.stationuuid,
                name: state.currentStation.name,
                url_resolved: state.currentStation.url_resolved,
                favicon: state.currentStation.favicon,
                tags: state.currentStation.tags,
                codec: state.currentStation.codec,
                bitrate: state.currentStation.bitrate,
                country: state.currentStation.country,
            }
        }
        UserConfigService.set(NAMESPACE, data)
    }, 300)
}

function _addToRecent(station) {
    if (!station?.stationuuid) return
    state.recentStations = [
        station,
        ...state.recentStations.filter(s => s.stationuuid !== station.stationuuid),
    ].slice(0, MAX_RECENT)
}

function _mapStation(s) {
    return {
        stationuuid: s.stationuuid,
        name: s.name,
        url_resolved: s.url_resolved || s.url,
        favicon: s.favicon || '',
        tags: s.tags || '',
        codec: s.codec || '',
        bitrate: s.bitrate || 0,
        country: s.country || '',
        language: s.language || '',
        votes: s.votes || 0,
        clickcount: s.clickcount || 0,
    }
}

function _mapTrack(t) {
    const file = t.file || {}
    return {
        id: t.id,
        title: t.title || '',
        artist: t.artist || '',
        duration: t.duration || 0,
        url: t.url || '',
        hasCover: !!t.has_cover,
        thumb: t.thumb || null,
        tags: t.tags || null,
        format: file.format || '',
        size: file.size || 0,
        sizeText: file.size_text || '',
        bitrate: file.bitrate || 0,
        sampleRate: file.sample_rate || 0,
        channels: file.channels || 0,
        createdAt: t.created_at || '',
    }
}

function _mapPlaylist(p) {
    return {
        id: p.id,
        name: p.name || '',
        isLibrary: !!p.is_library,
        sort: p.sort || 0,
        trackCount: p.track_count || 0,
        createdAt: p.created_at || '',
    }
}

async function _radioFetch(path, params = {}) {
    const url = new URL(RADIO_API + path)
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') url.searchParams.set(k, v)
    })
    const res = await fetch(url.toString(), {
        headers: { 'User-Agent': 'ServiceCP/1.0' },
    })
    if (!res.ok) throw new Error('Radio API error')
    return res.json()
}

function init() {
    if (state._initialized) return
    state._initialized = true
    _loadSaved()
    _ensureValidRadioCategory()
}

function _ensureValidRadioCategory() {
    if (state.activeTab === 'radio' && state.radioCategory === 'favorites' && !state.favorites.length) {
        state.radioCategory = 'genres'
    }
}

function onPanelOpen() {
    if (!state._initialized) init()
    _ensureValidRadioCategory()
    if (state.radioCategory === 'popular' && !state.stations.length) {
        fetchPopularStations()
    } else if (state.radioCategory === 'genres' && !state.stations.length) {
        const genre = state.radioGenre || Object.keys(GENRE_TAGS)[0]
        state.radioGenre = genre
        fetchStationsByTag(GENRE_TAGS[genre])
    }
    if (!state._libraryFetched) {
        loadPlaylists()
        loadTracks()
    }
}

async function loadPlaylists() {
    state.playlistsLoading = true
    try {
        const res = await ApiService.axios.get(endpoints.module_music_playlist)
        if (res.data?.status && Array.isArray(res.data.data)) {
            state.playlists = res.data.data.map(_mapPlaylist)
        }
    } catch { /* empty */ } finally {
        state.playlistsLoading = false
    }
}

async function loadTracks(page = 1, append = false) {
    const loadId = ++_trackLoadId
    state.libraryLoading = true
    try {
        const params = {
            page,
            page_limit: state._pageLimit,
        }
        if (state.activePlaylistId) params.playlist_id = state.activePlaylistId
        if (state.librarySearchQuery) params.search = state.librarySearchQuery
        const res = await ApiService.axios.get(endpoints.module_music_library, { params })
        if (loadId !== _trackLoadId) return
        if (res.data?.status) {
            let tracks = (res.data.data || []).map(_mapTrack)
            if (!append && !state.librarySearchQuery) {
                const order = _getTrackOrder(state.activePlaylistId)
                if (order) {
                    const orderMap = {}
                    order.forEach((id, i) => { orderMap[id] = i })
                    tracks.sort((a, b) => (orderMap[a.id] ?? 99999) - (orderMap[b.id] ?? 99999))
                }
            }
            state.libraryTracks = append ? [...state.libraryTracks, ...tracks] : tracks
            const pag = res.data.meta
            if (pag) {
                state.libraryPage = pag.current_page
                state.libraryHasMore = pag.has_more_page || false
            } else {
                state.libraryHasMore = false
            }
            state._libraryFetched = true
        }
    } catch { /* empty */ } finally {
        if (loadId === _trackLoadId) state.libraryLoading = false
    }
}

function loadMoreTracks() {
    if (state.libraryLoading || !state.libraryHasMore) return
    loadTracks(state.libraryPage + 1, true)
}

function updatePageLimit(height) {
    const rows = Math.ceil(height / 44)
    state._pageLimit = Math.max(DEFAULT_PAGE_LIMIT, rows + 10)
}

async function createPlaylist(name, trackIds) {
    try {
        const body = { name }
        if (trackIds?.length) body.track_ids = trackIds
        const res = await ApiService.axios.post(endpoints.module_music_playlist, body)
        if (res.data?.status && res.data.data) {
            state.playlists.push(_mapPlaylist(res.data.data))
            return res.data.data
        }
    } catch { /* empty */ }
    return null
}

async function updatePlaylist(id, data) {
    try {
        const res = await ApiService.axios.put(endpoints.module_music_playlist + '/' + id, data)
        if (res.data?.status && res.data.data) {
            const idx = state.playlists.findIndex(p => p.id === id)
            if (idx !== -1) state.playlists[idx] = _mapPlaylist(res.data.data)
            return res.data.data
        }
    } catch { /* empty */ }
    return null
}

async function deletePlaylist(id) {
    try {
        const res = await ApiService.axios.delete(endpoints.module_music_playlist + '/' + id)
        if (res.data?.status) {
            state.playlists = state.playlists.filter(p => p.id !== id)
            if (state.activePlaylistId === id) {
                state.activePlaylistId = null
                loadTracks()
            }
            return true
        }
    } catch { /* empty */ }
    return false
}

function setActivePlaylist(id) {
    state.activePlaylistId = id
    state.librarySearchQuery = ''
    clearTrackSelection()
    loadTracks()
    _save()
}

function searchLibrary(query) {
    state.librarySearchQuery = query
    clearTrackSelection()
    loadTracks()
}

async function addTrackToPlaylist(trackId, playlistId) {
    try {
        await fileManagerPicker.attachFiles('MODULE_MUSIC_PLAYLIST', playlistId, [trackId])
        await loadPlaylists()
        if (state.activePlaylistId === playlistId) await loadTracks()
        return true
    } catch { /* empty */ }
    return false
}

async function removeTrackFromPlaylist(trackId, playlistId) {
    try {
        await fileManagerPicker.detachFiles('MODULE_MUSIC_PLAYLIST', playlistId, [trackId], { confirm: false })
        await loadPlaylists()
        if (state.activePlaylistId === playlistId) await loadTracks()
        return true
    } catch { /* empty */ }
    return false
}

function toggleSelectMode() {
    state.selectMode = !state.selectMode
    if (!state.selectMode) state.selectedTrackIds = []
}

function toggleTrackSelect(id) {
    const idx = state.selectedTrackIds.indexOf(id)
    if (idx === -1) state.selectedTrackIds.push(id)
    else state.selectedTrackIds.splice(idx, 1)
}

function clearTrackSelection() {
    state.selectedTrackIds = []
    state.selectMode = false
}

function selectAllTracks() {
    state.selectedTrackIds = state.libraryTracks.map(t => t.id)
}

async function addTracksToPlaylist(ids, playlistId) {
    if (!ids.length || !playlistId) return false
    try {
        await fileManagerPicker.attachFiles('MODULE_MUSIC_PLAYLIST', playlistId, ids)
        await loadPlaylists()
        if (state.activePlaylistId === playlistId) await loadTracks()
        return true
    } catch { /* empty */ }
    return false
}

function saveTrackOrder(playlistId) {
    if (!state.libraryTracks.length) return
    const key = playlistId || '_library'
    const saved = UserConfigService.get(NAMESPACE)
    saved.trackOrders = saved.trackOrders || {}
    saved.trackOrders[key] = state.libraryTracks.map(t => t.id)
    UserConfigService.set(NAMESPACE, saved)
}

function _getTrackOrder(playlistId) {
    const key = playlistId || '_library'
    const saved = UserConfigService.get(NAMESPACE)
    const order = saved.trackOrders?.[key]
    return Array.isArray(order) ? order : null
}

async function removeTracksFromPlaylist(ids, playlistId) {
    if (!ids.length || !playlistId) return false
    try {
        await fileManagerPicker.detachFiles('MODULE_MUSIC_PLAYLIST', playlistId, ids, { confirm: false })
        await loadPlaylists()
        if (state.activePlaylistId === playlistId) await loadTracks()
        state.selectedTrackIds = state.selectedTrackIds.filter(id => !ids.includes(id))
        return true
    } catch { /* empty */ }
    return false
}

async function searchStations(query, opts = {}) {
    if (!query && !opts.tag) return
    state.radioLoading = true
    state.error = null
    try {
        const params = {
            limit: opts.limit || 30,
            offset: opts.offset || 0,
            order: 'clickcount',
            reverse: 'true',
            hidebroken: 'true',
        }
        if (query) params.name = query
        if (opts.country) params.country = opts.country
        if (opts.language) params.language = opts.language
        if (opts.tag) params.tag = opts.tag
        const data = await _radioFetch('/stations/search', params)
        state.stations = data.map(_mapStation)
    } catch { /* empty */ } finally {
        state.radioLoading = false
    }
}

async function fetchPopularStations(limit = 30) {
    state.radioLoading = true
    state.error = null
    try {
        const data = await _radioFetch('/stations/search', {
            country: 'Turkey',
            limit,
            order: 'clickcount',
            reverse: 'true',
            hidebroken: 'true',
        })
        state.stations = data.map(_mapStation)
    } catch { /* empty */ } finally {
        state.radioLoading = false
    }
}

async function fetchStationsByTag(tag, limit = 30) {
    state.radioLoading = true
    state.error = null
    try {
        const data = await _radioFetch('/stations/search', {
            tag,
            limit,
            order: 'clickcount',
            reverse: 'true',
            hidebroken: 'true',
        })
        state.stations = data.map(_mapStation)
    } catch { /* empty */ } finally {
        state.radioLoading = false
    }
}

function _registerClick(stationuuid) {
    if (!stationuuid) return
    _radioFetch('/url/' + stationuuid).catch(() => { /* empty */ })
}

function playStation(station) {
    if (!station?.url_resolved) return
    const audio = _getAudio()

    state.currentTrack = null
    state.duration = 0
    state.currentTime = 0
    state.error = null

    state.currentStation = station
    audio.src = station.url_resolved
    audio.volume = state.muted ? 0 : state.volume
    audio.play().catch(() => {
        state.error = 'streamError'
    })
    _addToRecent(station)
    _registerClick(station.stationuuid)
    _save()
}

function playTrack(track) {
    if (!track?.url) return
    const audio = _getAudio()

    state.currentStation = null
    state.error = null

    state.currentTrack = track
    audio.src = track.url
    audio.volume = state.muted ? 0 : state.volume
    audio.play().catch(() => {
        state.error = 'streamError'
    })
}

function togglePlay() {
    const audio = _getAudio()
    if (state.playing) {
        audio.pause()
    } else if (audio.src) {
        audio.play().catch(() => { /* empty */ })
    } else if (state.currentStation?.url_resolved) {
        audio.src = state.currentStation.url_resolved
        audio.play().catch(() => { /* empty */ })
    }
}

function stop() {
    const audio = _getAudio()
    audio.pause()
    audio.src = ''
    state.playing = false
    state.buffering = false
    state.currentStation = null
    state.currentTrack = null
    state.currentTime = 0
    state.duration = 0
}

function toggleDockAnimation() {
    state.dockAnimation = !state.dockAnimation
    _save()
}

function shutdown() {
    stop()
}

function setVolume(v) {
    state.volume = Math.max(0, Math.min(1, v))
    state.muted = false
    const audio = _getAudio()
    audio.volume = state.volume
    _save()
}

function toggleMute() {
    state.muted = !state.muted
    const audio = _getAudio()
    audio.volume = state.muted ? 0 : state.volume
    _save()
}

function seek(time) {
    const audio = _getAudio()
    if (isFinite(time) && isFinite(audio.duration)) {
        audio.currentTime = Math.max(0, Math.min(time, audio.duration))
    }
}

function nextTrack() {
    if (!state.currentTrack || !state.libraryTracks.length) return
    if (state.shuffle) {
        const others = state.libraryTracks.filter(t => t.id !== state.currentTrack.id)
        if (others.length) playTrack(others[Math.floor(Math.random() * others.length)])
        return
    }
    const idx = state.libraryTracks.findIndex(t => t.id === state.currentTrack.id)
    const next = state.libraryTracks[idx + 1]
    if (next) {
        playTrack(next)
    } else if (state.repeat === 'all') {
        playTrack(state.libraryTracks[0])
    } else {
        state.playing = false
        state.currentTime = 0
    }
}

function prevTrack() {
    if (!state.currentTrack || !state.libraryTracks.length) return
    if (state.currentTime > 3) {
        seek(0)
        return
    }
    const idx = state.libraryTracks.findIndex(t => t.id === state.currentTrack.id)
    const prev = state.libraryTracks[idx - 1]
    if (prev) playTrack(prev)
    else seek(0)
}

function toggleShuffle() {
    state.shuffle = !state.shuffle
    _save()
}

function toggleRepeat() {
    const modes = ['off', 'all', 'one']
    state.repeat = modes[(modes.indexOf(state.repeat) + 1) % 3]
    _save()
}

function addFavorite(station) {
    if (!station?.stationuuid) return
    if (state.favorites.some(s => s.stationuuid === station.stationuuid)) return
    if (state.favorites.length >= MAX_FAVORITES) return
    state.favorites.unshift(station)
    _save()
}

function removeFavorite(stationuuid) {
    state.favorites = state.favorites.filter(s => s.stationuuid !== stationuuid)
    _save()
}

function isFavorite(stationuuid) {
    return state.favorites.some(s => s.stationuuid === stationuuid)
}

function addFavoriteTrack(id) {
    if (!id || state.favoriteTracks.includes(id)) return
    state.favoriteTracks.push(id)
    _save()
}

function removeFavoriteTrack(id) {
    state.favoriteTracks = state.favoriteTracks.filter(t => t !== id)
    _save()
}

function isFavoriteTrack(id) {
    return state.favoriteTracks.includes(id)
}

function setRadioCategory(category) {
    state.radioCategory = category
    state.radioSearchQuery = ''
    state.stations = []
    if (category === 'popular') {
        fetchPopularStations()
    } else if (category === 'genres') {
        const firstGenre = Object.keys(GENRE_TAGS)[0]
        state.radioGenre = firstGenre
        fetchStationsByTag(GENRE_TAGS[firstGenre])
    }
    _save()
}

function setRadioGenre(genre) {
    state.radioGenre = genre
    if (genre) {
        fetchStationsByTag(GENRE_TAGS[genre] || genre)
    }
}

function setActiveTab(tab) {
    state.activeTab = tab
    _ensureValidRadioCategory()
    if (tab === 'library' && !state._libraryFetched) {
        loadPlaylists()
        loadTracks()
    }
    _save()
}

const libraryPlaylist = computed(() => {
    return state.playlists.find(p => p.isLibrary) || null
})

const currentSource = computed(() => {
    return state.currentStation || state.currentTrack || null
})

const displayName = computed(() => {
    if (state.currentStation) return state.currentStation.name
    if (state.currentTrack) return state.currentTrack.title || ''
    return ''
})

const displaySubtext = computed(() => {
    if (state.currentStation) {
        const parts = []
        if (state.currentStation.tags) {
            const first = state.currentStation.tags.split(',')[0].trim()
            if (first) parts.push(first)
        }
        if (state.currentStation.bitrate) parts.push(state.currentStation.bitrate + ' kbps')
        return parts.join(' · ')
    }
    if (state.currentTrack) {
        const parts = []
        if (state.currentTrack.artist) parts.push(state.currentTrack.artist)
        if (state.currentTrack.format) parts.push(state.currentTrack.format.toUpperCase())
        if (state.currentTrack.sizeText) parts.push(state.currentTrack.sizeText)
        return parts.join(' · ')
    }
    return ''
})

export default {
    state,
    GENRE_TAGS,

    libraryPlaylist,
    currentSource,
    displayName,
    displaySubtext,

    init,
    onPanelOpen,
    searchStations,
    fetchPopularStations,
    fetchStationsByTag,
    playStation,
    playTrack,
    togglePlay,
    stop,
    shutdown,
    toggleDockAnimation,
    setVolume,
    toggleMute,
    seek,
    nextTrack,
    prevTrack,
    toggleShuffle,
    toggleRepeat,
    addFavorite,
    removeFavorite,
    isFavorite,
    addFavoriteTrack,
    removeFavoriteTrack,
    isFavoriteTrack,
    setRadioCategory,
    setRadioGenre,
    setActiveTab,

    loadPlaylists,
    loadTracks,
    loadMoreTracks,
    updatePageLimit,
    createPlaylist,
    updatePlaylist,
    deletePlaylist,
    setActivePlaylist,
    searchLibrary,
    addTrackToPlaylist,
    removeTrackFromPlaylist,
    toggleSelectMode,
    toggleTrackSelect,
    clearTrackSelection,
    selectAllTracks,
    addTracksToPlaylist,
    removeTracksFromPlaylist,
    saveTrackOrder,
}
